import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { XCloseIcon, ForbiddenIcon } from '@rsos/assets/icons';
import { Box } from '@rsos/components/capstone/base';
import InputText from '@rsos/components/capstone/base/form/inputs/InputText';
import { updateAlertStatus } from '@rsos/reduxDataStore/src/features/alerts/chatSlice';
import { addFollower } from '@rsos/reduxDataStore/src/features/alerts/incidentsSlice';
import { DECLINED } from '../../../../constants/alertStatuses';
import ChatDecline from '../ChatDecline';
import {
  DeclineListWrapper,
  DeclineTitle,
  DeclineOtherReason,
  SendDeclineMessageButton,
} from './ChatDeclineList.styles';
import {
  trackClickDeclineReason,
  trackSendDeclineReason,
} from './chatDeclineListTrackings';

const declineReasons = [
  'Wrong Jurisdiction',
  'Address Incorrect',
  'Responder Required for Dispatch',
  'ECC Requires Confirmed Emergency',
  'Not Accepting Alarms',
  'Information Incomplete',
  'No Permit #',
  'Repetitive Notifications',
  'On the No Response List',
];

const ENTER_KEY = 13;

/**
 * @param {function} onRemoveAlertFromQueue - function that updates the state when an alert is
 *  removed from the queue
 * @param {number} paneWidth - the current width of the decline pane
 * @param {function} onShowDeclinePane - function that toggles the display of the decline pane
 * @param {object} theme - styled components theme object
 */
const ChatDeclineList = ({
  onRemoveAlertFromQueue,
  paneWidth,
  onShowDeclinePane,
}) => {
  const dispatch = useDispatch();
  const {
    currentOrg,
    profile: { id: userID },
  } = useSelector(state => state.sinatra.user);
  const irpVersion = useSelector(state => state.sinatra.irp.irpVersion);
  const selectedAlertID = useSelector(state => state.incidents.selectedAlertID);
  const declineInputRef = useRef();
  const [selectedReason, setSelectedReason] = useState('');
  const [isRequestPending, setIsRequestPending] = useState(false);

  const handleInputOnChange = e => {
    e.preventDefault();
    setSelectedReason(e.target.value);
  };

  const clearInput = () => {
    declineInputRef.current.value = '';
  };

  const handleSubmitDeclineReason = () => {
    setIsRequestPending(true);

    const declineMessage = declineInputRef.current.value;

    const updatedStatusInfo = {
      alertID: selectedAlertID,
      org: currentOrg.name,
      status: DECLINED,
      decline_reason: declineMessage,
    };

    if (declineInputRef.current.value) {
      dispatch(updateAlertStatus(updatedStatusInfo)).finally(() =>
        setIsRequestPending(false),
      );

      dispatch(addFollower(userID));
      clearInput();
      onShowDeclinePane(false);
    }
    trackSendDeclineReason(declineMessage, selectedAlertID, irpVersion);
    onRemoveAlertFromQueue();
  };

  const handleOnKeyDown = e => {
    if (e.keyCode === ENTER_KEY && !e.shiftKey) {
      handleSubmitDeclineReason(e);
    }
  };

  const handleOnClickDeclineReason = reason => {
    trackClickDeclineReason(reason, selectedAlertID, irpVersion);
    setSelectedReason(reason);
  };

  return (
    <DeclineListWrapper paneWidth={paneWidth}>
      <Box horizontal justify={'space-between'}>
        <ForbiddenIcon color="secondaryText" width={25} height={25} />
        <Box onClick={() => onShowDeclinePane(false)} cursor="pointer">
          <XCloseIcon color="secondaryText" />
        </Box>
      </Box>
      <DeclineTitle>Why are you declining this alarm?</DeclineTitle>
      {declineReasons.map((reason, idx) => (
        <ChatDecline
          reason={reason}
          key={idx}
          handleClickOnReason={() => handleOnClickDeclineReason(reason)}
        />
      ))}
      <DeclineOtherReason>Other Reasons</DeclineOtherReason>
      <Box position="relative">
        <InputText
          size="small"
          inputRef={declineInputRef}
          onChange={e => handleInputOnChange(e)}
          onKeyDown={e => handleOnKeyDown(e)}
          placeholder={'Other reason'}
          value={selectedReason}
          shouldDisplayClearButton={false}
        />
        <SendDeclineMessageButton
          onClick={() => !isRequestPending && handleSubmitDeclineReason()}
        >
          Submit
        </SendDeclineMessageButton>
      </Box>
      <DeclineOtherReason onClick={() => onShowDeclinePane(false)}>
        Cancel
      </DeclineOtherReason>
    </DeclineListWrapper>
  );
};

ChatDeclineList.propTypes = {
  onRemoveAlertFromQueue: PropTypes.func.isRequired,
  paneWidth: PropTypes.number,
  onShowDeclinePane: PropTypes.func.isRequired,
};

export default ChatDeclineList;
